/* eslint-disable no-plusplus */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useAlert from 'utils/alert';

interface IWindow extends Window {
  webkitSpeechRecognition: any;
  SpeechRecognition: any;
}

type UseMicrophoneParams = {
  onRecordStart: () => void,
  onRecordStop: (text: string) => void,
  onRecordError?: () => void,
}

export const useSpeechRecognition = ({ onRecordStart, onRecordStop, onRecordError }: UseMicrophoneParams) => {
  const showAlert = useAlert();
  const [isRecording, setIsRecording] = useState(false);
  const { i18n } = useTranslation();
  const transcriptRef = useRef<string>('');
  const recognitionRef = useRef<any>(null);
  const stoppedByUserRef = useRef<boolean>(false);
  const unmountedRef = useRef<boolean>(false);

  // Cleanup on unmount: prevent callbacks after component unmounts
  useEffect(() => {
    return () => {
      unmountedRef.current = true;
      if (recognitionRef.current) {
        try {
          recognitionRef.current.onend = null;
          recognitionRef.current.onerror = null;
          recognitionRef.current.onresult = null;
          recognitionRef.current.stop();
        } catch (e) {
          // Silently ignore errors during cleanup
        }
      }
    };
  }, []);

  const stopRecordAudio = () => {
    if (!recognitionRef.current) {
      // Ensure idle state even if recognizer is null
      setIsRecording(false);
      return;
    }

    stoppedByUserRef.current = true;
    recognitionRef.current.stop();
    setIsRecording(false);
  };

  const startRecordAudio = async () => {
    try {
      // Guard against duplicate starts
      if (isRecording || recognitionRef.current) {
        return;
      }

      const { webkitSpeechRecognition, SpeechRecognition } = window as unknown as IWindow;
      const SelectedRecognition = SpeechRecognition || webkitSpeechRecognition;
      if (!SelectedRecognition) return;

      transcriptRef.current = '';
      stoppedByUserRef.current = false;
      const recognition = new SelectedRecognition();
      recognitionRef.current = recognition;

      recognition.lang = i18n.language;
      recognition.continuous = true;
      recognition.interimResults = false;
      recognition.maxAlternatives = 1;
      recognition.onresult = (event: any) => {
        if (unmountedRef.current) return;
        let fullTranscript = '';
        for (let i = 0; i < event.results.length; i++) {
          if (event.results[i].isFinal) {
            fullTranscript += event.results[i][0].transcript;
          }
        }
        transcriptRef.current = fullTranscript.toLowerCase().trim();
      };
      recognition.onend = () => {
        if (unmountedRef.current) return;
        recognitionRef.current = null;

        if (stoppedByUserRef.current) {
          // User explicitly stopped: deliver transcript once and idle
          const finalText = transcriptRef.current.trim();
          transcriptRef.current = '';
          stoppedByUserRef.current = false;
          if (finalText) {
            onRecordStop(finalText);
          }
        } else {
          // Browser auto-ended (not user-initiated): idle without submitting transcript
          setIsRecording(false);
          transcriptRef.current = '';
          stoppedByUserRef.current = false;
        }
      };
      recognition.onerror = () => {
        if (unmountedRef.current) return;
        showAlert('Unexpected error occurred while trying to use the microphone', 'error');
        setIsRecording(false);
        transcriptRef.current = '';
        recognitionRef.current = null;
        stoppedByUserRef.current = false;
        if (onRecordError) onRecordError();
      };
      recognition.start();
      setIsRecording(true);
      onRecordStart();
    } catch (error) {
      showAlert('Unexpected error occurred while trying to use the microphone', 'error');
      setIsRecording(false);
      if (onRecordError) {
        onRecordError();
      }
    }
  };

  return {
    isRecording,
    startRecordAudio,
    stopRecordAudio
  };
};
